import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { DateTime } from "luxon";
import { useEffect } from "react";
import { Helmet } from "react-helmet";
import { Link, useParams } from "react-router-dom";

import { LISBlock, LISCase, LISCaseList } from "pwapi";

import {
  useHasLoaded,
  useRequestAfterEverySiteLoad,
} from "shared/hooks/requestStatusHooks";

import { listCases } from "app/actions/caseActions";
import { getShipment } from "app/actions/shippingActions";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { ReactComponent as LogoSVG } from "assets/logo.svg";
import {
  useSelectedLab,
  useSelectedLabStation,
} from "hooks/useSelectedLabStation";
import AccessionNumber from "ui/components/AccessionNumber";
import AppSpinner from "ui/components/AppSpinner";
import ClinicName from "ui/components/ClinicName";
import UserFullName from "ui/components/UserFullName";
import { blockSort, specimenSort } from "util/sortFunctions";

const Shipment = () => {
  const dispatch = useAppDispatch();
  const shipment_id = Number(useParams().shipment_id);
  const selectedLabStation = useSelectedLabStation();
  const lab = useSelectedLab();

  const clinics = useAppSelector((sys) => sys.clinics);
  const users = useAppSelector((sys) => sys.users);

  const columnHelper = createColumnHelper<LISCaseList>();

  const cases = useAppSelector((sys) => sys.casesWithWork);
  const casesReady = useRequestAfterEverySiteLoad(listCases, {
    parentLookupLabStationId: selectedLabStation.id,
  });
  const shipment = useAppSelector((sys) => sys.shipment);
  const shipmentReady = useHasLoaded(getShipment, {
    parentLookupLabStationId: selectedLabStation?.id,
    id: shipment_id,
  });
  const shipping_date = shipment?.shipped_at || shipment?.created_at;

  useEffect(() => {
    selectedLabStation?.id &&
      shipment_id &&
      dispatch(
        getShipment({
          parentLookupLabStationId: selectedLabStation?.id,
          id: shipment_id,
        })
      );
  }, [selectedLabStation?.id, shipment_id]);

  const shippedBlocks = shipment?.shipment_items
    ?.filter((i) => i.item_type === "block")
    ?.map((i) => i.object_id);
  const shippedCases = Object.values(cases)
    .map((c) => {
      const includesBlocks = c.blocks.filter(
        (b) => b.id && shippedBlocks?.includes(b.id)
      );
      if (includesBlocks.length) {
        return {
          ...c,
          blocks: includesBlocks as LISBlock[],
        };
      }
      return undefined;
    })
    .filter((c) => c) as LISCase[];

  const shipment_date =
    shipment?.shipped_at &&
    DateTime.fromISO(shipment.shipped_at).toLocaleString();

  const columns = [
    columnHelper.accessor((row) => `${row.accession}`, {
      id: "Accession",
      cell: ({ row }) => {
        return <AccessionNumber accession={row.original.accession} />;
      },
      sortingFn: "alphanumeric",
    }),

    columnHelper.accessor(
      (row) =>
        `${row.patient?.last_name}, ${row.patient?.first_name} ${
          row.patient?.date_of_birth ?? ""
        } ${row.accession ?? ""} ${row.external_order_id ?? ""}`,
      {
        cell: ({ row }) => {
          const { patient } = row.original;
          return (
            <>
              <strong>
                {patient?.last_name}, {patient?.first_name}
              </strong>
              <br />
              {patient?.date_of_birth && (
                <span>
                  {DateTime.fromFormat(
                    patient?.date_of_birth,
                    "yyyy-MM-dd"
                  ).toLocaleString()}
                </span>
              )}
            </>
          );
        },
        id: "Patient",
      }
    ),

    columnHelper.accessor(
      (row) => {
        const ordering_doctor = users[row.ordering_doctor_id || 0];
        return `${clinics[row.patient?.clinic_id || 0]?.name} ${
          ordering_doctor?.first_name
        } ${ordering_doctor?.last_name}`;
      },
      {
        cell: ({ row }) => {
          return (
            <>
              <strong>
                <ClinicName id={row.original.patient?.clinic_id} />
              </strong>
              <br />
              <span>
                <UserFullName id={row.original.ordering_doctor_id} />
              </span>
            </>
          );
        },
        id: "Provider",
        enableColumnFilter: true,
        filterFn: "includesString",
      }
    ),

    columnHelper.accessor(
      (row) =>
        row.blocks
          ?.map(
            (b) =>
              `${
                row.specimens.find((s) => s.id === b.specimen_id)?.name || ""
              }${b.name || ""}`
          )
          .join(" "),
      {
        id: "Blocks",
        cell: ({ row }) => {
          const specimens = [...row.original.specimens].sort(specimenSort);
          return (
            <div>
              {specimens?.map((s) => (
                <div key={s.id}>
                  {row.original.blocks
                    .filter((b) => b.specimen_id === s.id)
                    .sort(blockSort)
                    .map((b) => `${s.name || ""}${b.name || ""}`)
                    .join(", ")}
                </div>
              ))}
            </div>
          );
        },
      }
    ),
  ];

  const table = useReactTable({
    data: shippedCases,
    columns,

    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),

    enableSorting: true,
    state: {
      sorting: [
        {
          id: "Accession",
          desc: false,
        },
      ],
    },
  });

  if (!casesReady) return <AppSpinner>Loading Cases...</AppSpinner>;

  if (!shipmentReady) return <AppSpinner>Loading Shipments...</AppSpinner>;

  return (
    <div className="Cases Shipping">
      <Helmet>
        <title>{`Shipment ${shipment?.id}: ${shipment_date}`}</title>
      </Helmet>
      <header className="Cases__header">
        <figure className="Logo">
          <LogoSVG />
        </figure>

        <div>
          <p>
            <strong>{lab?.name}</strong>
          </p>
          <p className="Screen">Shipping</p>
          <p className="Print">
            <strong>
              <code>Shipping Manifest</code>
            </strong>{" "}
            <span>{shipment_date}</span>
          </p>
        </div>
      </header>
      <nav>
        <Link to="/cases">Cases</Link>
        <Link to="/users">Users</Link>
        <Link to="/shipping">
          <strong>Shipping</strong>
        </Link>
      </nav>

      <div className="ShippingManifest">
        <aside className="Shipping__stats">
          {shippedCases.reduce((sum, c) => sum + c.blocks?.length ?? 0, 0)}{" "}
          Blocks across {shippedCases.length} Cases, Shipped{" "}
          {shipping_date && DateTime.fromISO(shipping_date).toLocaleString()} by{" "}
          <UserFullName id={shipment?.shipped_by_id} />
        </aside>

        <div className="Cases__list">
          <table>
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th key={header.id}>
                      {header.isPlaceholder ? null : (
                        <div
                          {...{
                            className: header.column.getCanSort()
                              ? "cursor-pointer select-none"
                              : "",
                            onClick: header.column.getToggleSortingHandler(),
                          }}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                        </div>
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      className={`Cases__td Cases__td--${cell}`}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Shipment;
